import { useEffect, useState } from "react";

import {
  FLAGS,
  useFeatureFlag,
  useFeatureFlagsReady,
} from "../featureFlags";
import {
  HOMEPAGE_VERSION,
  HOMEPAGE_VERSION_EVENT,
  HOMEPAGE_VERSION_KEY,
  isHomepageChoiceAvailable,
  readHomepageVersion,
  writeHomepageVersion,
} from "../utils/homepageVersion";

/**
 * The paid student's homepage choice — the new homepage shared with
 * StudentPortal, or the old Sandbox one — kept in sync across every reader.
 *
 * Re-reads when the choice changes in this tab (HOMEPAGE_VERSION_EVENT), in
 * another tab (storage), or when the session itself changes, since the choice
 * is stored per user.
 *
 * Nobody can choose until the `sandbox_new_homepage` flag is on — and known to
 * be, for this student (flags ready): no banner, no switch, and /home stays the
 * old homepage.
 *
 * @returns {{
 *   isNewHomepage: boolean,
 *   isChoiceAvailable: boolean,
 *   setHomepageVersion: (version: "new"|"old") => void,
 * }}
 *   isChoiceAvailable — new homepage released (flag) and a paid student on
 *   standalone Sandbox; the only case that gets the banner and the switch.
 */
const useHomepageVersion = () => {
  const [version, setVersion] = useState(readHomepageVersion);
  const isNewHomepageReleased = useFeatureFlag(FLAGS.SANDBOX_NEW_HOMEPAGE.name);
  const areFlagsReady = useFeatureFlagsReady();

  useEffect(() => {
    const sync = () => setVersion(readHomepageVersion());
    const onStorage = (event) => {
      if (event.key === HOMEPAGE_VERSION_KEY) sync();
    };
    window.addEventListener(HOMEPAGE_VERSION_EVENT, sync);
    window.addEventListener("sandbox_user_update", sync);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(HOMEPAGE_VERSION_EVENT, sync);
      window.removeEventListener("sandbox_user_update", sync);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return {
    isNewHomepage: version === HOMEPAGE_VERSION.NEW,
    isChoiceAvailable:
      areFlagsReady && isNewHomepageReleased && isHomepageChoiceAvailable(),
    setHomepageVersion: writeHomepageVersion,
  };
};

export default useHomepageVersion;
